import useSWR from 'swr';
import { api, getUserToken } from '@utils/api';
import { useAuthStore } from '@store/authStore';

/**
 * Hook to fetch and check VIP status
 * Uses user data from auth store for immediate access
 * @returns {object} { isVIP, vipInfo, isLoading, error, mutate }
 */
export const useVIPStatus = () => {
    const userToken = getUserToken();
    const { user: authUser } = useAuthStore();

    const { data, error, isLoading, mutate } = useSWR(
        userToken ? [api('/api/user/vip-status'), userToken] : null,
        async ([url, token]) => {
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch VIP status');
            }

            const result = await response.json();
            
            // Cache VIP status in localStorage for instant next load
            const vipStatusData = result.data || result;
            if (vipStatusData) {
                try {
                    localStorage.setItem('vip-status-cache', JSON.stringify({
                        data: vipStatusData,
                        timestamp: Date.now()
                    }));
                } catch (e) {
                    // Silent fail for cache
                }
            }
            
            return vipStatusData;
        },
        {
            revalidateOnFocus: false,
            dedupingInterval: 60000, // 1 minute
            // Use cached data as fallback while loading
            fallbackData: (() => {
                try {
                    const cached = localStorage.getItem('vip-status-cache');
                    if (cached) {
                        const parsed = JSON.parse(cached);
                        // Use cache if less than 5 minutes old
                        if (Date.now() - parsed.timestamp < 5 * 60 * 1000) {
                            return parsed.data;
                        }
                    }
                } catch (e) {
                    // Silent fail for cache read
                }
                return undefined;
            })()
        }
    );

    // Priority: API data > cached data > authUser
    const vipData = data || authUser;

    // Check if VIP - UPDATED to match Supabase naming
    const vipStatus = vipData?.vip_status;
    
    // Database uses: 'vip_permanent', 'vip_temp', 'regular'
    const isPermanent = vipStatus === 'vip_permanent' || vipStatus === 'permanent';
    const isTempVIP = vipStatus === 'vip_temp' || vipStatus === 'active' || vipStatus === 'vip';
    const isVIPActive = isPermanent || isTempVIP || vipData?.is_vip === true;
    
    // Check expiration only for temporary VIP
    const expiresAt = vipData?.vip_expires_at;
    const isExpired = isTempVIP && expiresAt && new Date(expiresAt) < new Date();

    // Final check: permanent VIP always true, temp VIP check expiration
    const finalIsVIP = isPermanent || (isTempVIP && !isExpired);

    // ⚠️ IMPORTANT: Don't show false status while still loading
    // Keep previous cached value during loading to prevent flicker
    const displayIsVIP = isLoading && !vipData ? null : finalIsVIP;

    return {
        isVIP: displayIsVIP !== null ? displayIsVIP : false, // Default to false only if not loading
        vipInfo: vipData || null,
        vipExpiresAt: expiresAt || null,
        isLoading,
        error,
        mutate,
    };
};

export default useVIPStatus;
